function renderSingleActionsContentTemplate({
  buildAuthorshipUserTypeBuildAuthorshipUserType
}: {
  buildAuthorshipUserTypeBuildAuthorshipUserType: string
}): string {
  return `${buildAuthorshipUserTypeBuildAuthorshipUserType}\n`
}

function renderSingleActionsRowsTemplate({
  fieldName,
  relationIdCol,
  junctionVar,
  schemaIdCol
}: {
  fieldName: string
  relationIdCol: string
  junctionVar: string
  schemaIdCol: string
}): string {
  return `    const ${fieldName}Rows = await db
      .select({ ${relationIdCol}: ${junctionVar}.${relationIdCol} })
      .from(${junctionVar})
      .where(eq(${junctionVar}.${schemaIdCol}, SINGLETON_ID))`
}

function renderSingleActionsServerActionTemplate({
  join,
  cacheReadImport,
  cacheTagsVar,
  singular,
  hasSelectFieldsSelectOptionValue,
  authorshipAliasBlock,
  singletonId,
  formatActionDateSource,
  selectResolutionFunctions,
  cacheLifeLine,
  selectClause,
  tableVar,
  singleRowReturn,
  singularText
}: {
  join: string
  cacheReadImport: string
  cacheTagsVar: string
  singular: string
  hasSelectFieldsSelectOptionValue: string
  authorshipAliasBlock: string
  singletonId: string
  formatActionDateSource: string
  selectResolutionFunctions: string
  cacheLifeLine: string
  selectClause: string
  tableVar: string
  singleRowReturn: string
  singularText: string
}): string {
  return `'use server'

import db from '@admin/db'
import { ${join} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'
${cacheReadImport}
import { ${cacheTagsVar} } from './types'
import type { ${singular}${hasSelectFieldsSelectOptionValue} } from './types'

${authorshipAliasBlock}

const SINGLETON_ID = '${singletonId}'

${formatActionDateSource}
${selectResolutionFunctions}
export async function get${singular}(): Promise<${singular} | null> {
  'use cache'${cacheLifeLine}
  cacheTag(${cacheTagsVar}.all)

  try {
    const result = await ${selectClause}.where(eq(${tableVar}.id, SINGLETON_ID)).limit(1)
    if (result.length === 0) return null
    ${singleRowReturn}
  } catch (error) {
    console.error('Error fetching ${singularText}:', error)
    throw new Error('Failed to fetch ${singularText}')
  }
}
`
}

function renderSingleActionsContentDetailsTemplate({
  junctionVar,
  schemaIdCol,
  inputIdsVar,
  relationIdVar,
  relationIdCol
}: {
  junctionVar: string
  schemaIdCol: string
  inputIdsVar: string
  relationIdVar: string
  relationIdCol: string
}): string {
  return `      await tx.delete(${junctionVar}).where(eq(${junctionVar}.${schemaIdCol}, SINGLETON_ID))
      if (${inputIdsVar}.length > 0) {
        await tx.insert(${junctionVar}).values(
          ${inputIdsVar}.map((${relationIdVar}) => ({
            ${schemaIdCol}: SINGLETON_ID,
            ${relationIdCol}: ${relationIdVar}
          }))
        )
      }`
}

function renderSingleActionsServerActionSerializedValueTemplate({
  join,
  cacheInvalidationFn,
  singular,
  kebabSingular,
  cacheTagsVar,
  markdownImport,
  singletonId,
  fieldMeta,
  m2mInputDeclarationsContent,
  htmlUpsertBlock,
  tableVar,
  schemaName,
  auditCategory,
  stringify,
  m2mWriteStatementsContent,
  camelSingular,
  singularText
}: {
  join: string
  cacheInvalidationFn: string
  singular: string
  kebabSingular: string
  cacheTagsVar: string
  markdownImport: string
  singletonId: string
  fieldMeta: string
  m2mInputDeclarationsContent: string
  htmlUpsertBlock: string
  tableVar: string
  schemaName: string
  auditCategory: string
  stringify: string
  m2mWriteStatementsContent: string
  camelSingular: string
  singularText: string
}): string {
  return `'use server'

import { requireRole, UserRole } from '@admin/auth/middleware'
import { getAuditRequestMetadata } from '@admin/actions/audit/get-audit-request-metadata'
import db from '@admin/db'
import { ${join} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { runAfterHooks, runBeforeHooks } from '@admin/lib/lifecycle-hooks'
import { getChangedFieldNames } from '@admin/utils/audit/get-changed-field-names'
import { getTouchedFieldNames } from '@admin/utils/audit/get-touched-field-names'
import { ${cacheInvalidationFn} } from 'next/cache'
import { get${singular} } from './get-${kebabSingular}'
import { ${cacheTagsVar} } from './types'
import type { ${singular}UpsertInput, ${singular}UpsertResult, ${singular} } from './types'${markdownImport}

const SINGLETON_ID = '${singletonId}'

export async function upsert${singular}(input: ${singular}UpsertInput): Promise<${singular}UpsertResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const actorId = actor.id
    const fieldMeta = [
    ${fieldMeta}
    ]
${m2mInputDeclarationsContent}

    const processedData: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(input)) {
      const field = fieldMeta.find(f => f.name === key)
      if (!field) continue
      if (field.type === 'list') {
        processedData[key] = value || []
      } else if (!field.required && ['number', 'decimal'].includes(field.type)) {
        processedData[key] =
          value === undefined ||
          value === null ||
          value === '' ||
          (typeof value === 'number' && Number.isNaN(value))
            ? null
            : value
      } else if (!field.required && ['date', 'timestamp', 'time', 'string', 'email', 'varchar', 'text', 'select', 'relationship'].includes(field.type)) {
        processedData[key] = value && value !== '' ? value : null
      } else {
        processedData[key] = value
      }
    }
${htmlUpsertBlock}
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const [previousRow] = await db
      .select()
      .from(${tableVar})
      .where(eq(${tableVar}.id, SINGLETON_ID))
      .limit(1)
    const changedFields = previousRow
      ? getChangedFieldNames(previousRow as unknown as Record<string, unknown>, processedData)
      : getTouchedFieldNames(processedData)

    const lifecycleContext = {
      user: actor,
      entityType: '${schemaName}',
      schemaType: 'single' as const,
      auditCategory: '${auditCategory}' as const,
      timestamp: now,
      sourceType: 'admin_ui' as const,
      ipAddress: auditRequestMetadata.ipAddress,
      userAgent: auditRequestMetadata.userAgent,
      entityId: SINGLETON_ID,
      input: processedData,
      changedFields,
      targetLabel: ${stringify}
    }
    await runBeforeHooks(previousRow ? 'beforeUpdate' : 'beforeCreate', lifecycleContext)

    const result = await db.transaction(async (tx) => {
      const rows = await tx
        .insert(${tableVar})
        .values({
          id: SINGLETON_ID,
          ...processedData,
          createdAt: now,
          updatedAt: now,
          createdBy: actorId,
          updatedBy: actorId
        })
        .onConflictDoUpdate({
          target: ${tableVar}.id,
          set: { ...processedData, updatedAt: now, updatedBy: actorId }
        })
        .returning()
${m2mWriteStatementsContent}
      return rows
    })

    ${cacheInvalidationFn}(${cacheTagsVar}.all)
    const saved${singular} = await get${singular}()
    runAfterHooks(previousRow ? 'afterUpdate' : 'afterCreate', {
      ...lifecycleContext,
      data: (saved${singular} ?? result[0]) as unknown as Record<string, unknown>
    })

    return {
      success: true,
      ${camelSingular}: saved${singular} ?? (result[0] as unknown as ${singular})
    }
  } catch (error) {
    console.error('Error upserting ${singularText}:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to save ${singularText}')
  }
}
`
}

function renderSingleActionsContentDetailsBranchTemplate({
  singular,
  kebabSingular
}: {
  singular: string
  kebabSingular: string
}): string {
  return `
export { get${singular}SelectOptions } from './get-${kebabSingular}-select-options'
export { create${singular}SelectOption } from './create-${kebabSingular}-select-option'
`
}

function renderSingleActionsContentDetailsBranchBodyTemplate({
  singular,
  selectOptionTypeExports,
  kebabSingular,
  selectOptionFunctionExports
}: {
  singular: string
  selectOptionTypeExports: string
  kebabSingular: string
  selectOptionFunctionExports: string
}): string {
  return `export type { ${singular}, ${singular}UpsertInput, ${singular}UpsertResult${selectOptionTypeExports} } from './types'
export { get${singular} } from './get-${kebabSingular}'
export { upsert${singular} } from './upsert-${kebabSingular}'${selectOptionFunctionExports}
`
}

export function renderSingleActionsSingleActionsRenderer(
  request:
    | {
        kind: 'renderSingleActionsContentTemplate'
        props: {
          buildAuthorshipUserTypeBuildAuthorshipUserType: string
        }
      }
    | {
        kind: 'renderSingleActionsRowsTemplate'
        props: {
          fieldName: string
          relationIdCol: string
          junctionVar: string
          schemaIdCol: string
        }
      }
    | {
        kind: 'renderSingleActionsServerActionTemplate'
        props: {
          join: string
          cacheReadImport: string
          cacheTagsVar: string
          singular: string
          hasSelectFieldsSelectOptionValue: string
          authorshipAliasBlock: string
          singletonId: string
          formatActionDateSource: string
          selectResolutionFunctions: string
          cacheLifeLine: string
          selectClause: string
          tableVar: string
          singleRowReturn: string
          singularText: string
        }
      }
    | {
        kind: 'renderSingleActionsContentDetailsTemplate'
        props: {
          junctionVar: string
          schemaIdCol: string
          inputIdsVar: string
          relationIdVar: string
          relationIdCol: string
        }
      }
    | {
        kind: 'renderSingleActionsServerActionSerializedValueTemplate'
        props: {
          join: string
          cacheInvalidationFn: string
          singular: string
          kebabSingular: string
          cacheTagsVar: string
          markdownImport: string
          singletonId: string
          fieldMeta: string
          m2mInputDeclarationsContent: string
          htmlUpsertBlock: string
          tableVar: string
          schemaName: string
          auditCategory: string
          stringify: string
          m2mWriteStatementsContent: string
          camelSingular: string
          singularText: string
        }
      }
    | {
        kind: 'renderSingleActionsContentDetailsBranchTemplate'
        props: {
          singular: string
          kebabSingular: string
        }
      }
    | {
        kind: 'renderSingleActionsContentDetailsBranchBodyTemplate'
        props: {
          singular: string
          selectOptionTypeExports: string
          kebabSingular: string
          selectOptionFunctionExports: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderSingleActionsContentTemplate':
      return renderSingleActionsContentTemplate(request.props)
    case 'renderSingleActionsRowsTemplate':
      return renderSingleActionsRowsTemplate(request.props)
    case 'renderSingleActionsServerActionTemplate':
      return renderSingleActionsServerActionTemplate(request.props)
    case 'renderSingleActionsContentDetailsTemplate':
      return renderSingleActionsContentDetailsTemplate(request.props)
    case 'renderSingleActionsServerActionSerializedValueTemplate':
      return renderSingleActionsServerActionSerializedValueTemplate(request.props)
    case 'renderSingleActionsContentDetailsBranchTemplate':
      return renderSingleActionsContentDetailsBranchTemplate(request.props)
    case 'renderSingleActionsContentDetailsBranchBodyTemplate':
      return renderSingleActionsContentDetailsBranchBodyTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
